import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  InputAdornment, CircularProgress, Alert
} from '@mui/material';
import { Add, Edit, Delete, Search, Replay, People } from '@mui/icons-material';
import api from '../services/api';

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/sessions');
      setSessions(res.data);
    } catch (err) {
      setError('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await api.delete(`/sessions/${id}`);
      setSessions(sessions.filter(s => s._id !== id));
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const filteredSessions = sessions.filter(s =>
    s.title?.toLowerCase().includes(search.toLowerCase()) ||
    s.presenters?.some(p => p.name?.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isPast = (date) => new Date(date) < new Date();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Sessions SkinKnowledge</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/sessions/new')}
        >
          Nouvelle Session
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher une session..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Presentateurs</TableCell>
              <TableCell>Moderateurs</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucune session trouvee
                </TableCell>
              </TableRow>
            ) : (
              filteredSessions.map((session) => (
                <TableRow key={session._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(session.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.title}
                    </Typography>
                    {session.replayUrl && (
                      <Chip
                        icon={<Replay />}
                        label="Replay"
                        size="small"
                        color="success"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <People fontSize="small" color="action" />
                      <Typography variant="body2">
                        {session.presenters?.length || 0}
                      </Typography>
                    </Box>
                    {session.presenters?.slice(0, 2).map((p, i) => (
                      <Typography key={i} variant="caption" display="block" color="text.secondary">
                        {p.name}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.moderators?.length || 0} moderateur(s)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isPast(session.date) ? (
                      <Chip label="Passee" size="small" color="default" />
                    ) : (
                      <Chip label="A venir" size="small" color="primary" />
                    )}
                    {!session.isPublished && (
                      <Chip label="Brouillon" size="small" color="warning" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/sessions/${session._id}`)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(session._id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {filteredSessions.length} session(s) - {filteredSessions.filter(s => isPast(s.date)).length} passee(s)
        </Typography>
      </Box>
    </Box>
  );
}
